import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { ColumnDef, DataTableComponent } from '../../../shared/components/data-table/data-table';
import { CreateProgramModalComponent } from './create-program-modal';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal';
import { Program, ProgramType } from '../../../shared/models/program.models';
import { ProgramService } from '../../../core/services/program.service';

@Component({
  selector: 'app-programs',
  imports: [NgClass, DataTableComponent, CreateProgramModalComponent, ConfirmModalComponent],
  templateUrl: './programs.html',
})
export class ProgramsComponent implements OnInit {
  private programService = inject(ProgramService);
  private destroyRef = inject(DestroyRef);

  protected programs = signal<Program[]>([]);
  protected programTypes = signal<ProgramType[]>([]);
  protected loading = signal(false);
  protected total = signal(0);
  protected pageIndex = signal(0);
  protected readonly pageSize = 8;

  protected showModal = signal(false);
  protected selectedProgram = signal<Program | null>(null);
  protected programToDelete = signal<Program | null>(null);
  protected selectedTypeId = signal<string>('');
  protected searchQuery = signal<string>('');

  protected readonly columns: ColumnDef<Program>[] = [
    {
      key: 'name',
      header: 'Program',
      cell: (r) => r.name,
      cellClass: 'font-semibold text-navy-900',
    },
    {
      key: 'situationTaxonomy',
      header: 'Situation Taxonomy',
      cell: (r) => r.situationTaxonomy?.name ?? '—',
      cellClass: 'text-navy-900',
    },
  ];

  ngOnInit(): void {
    this.loadProgramTypes();
  }

  private loadProgramTypes(): void {
    this.programService
      .getProgramTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.programTypes.set(res.programTypes);
          if (res.programTypes.length) this.selectedTypeId.set(res.programTypes[0].id);
          this.load();
        },
      });
  }

  protected load(): void {
    this.loading.set(true);
    this.programService
      .getPrograms(
        { pageSize: this.pageSize, pageIndex: this.pageIndex(), filter: this.searchQuery() },
        this.selectedTypeId() || undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.programs.set(res.programs);
          this.total.set(res.total);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected selectType(id: string): void {
    this.selectedTypeId.set(id);
    this.pageIndex.set(0);
    this.load();
  }

  protected onPageChange(page: number): void {
    this.pageIndex.set(page - 1);
    this.load();
  }

  protected openCreate(): void {
    this.selectedProgram.set(null);
    this.showModal.set(true);
  }

  protected onRowAction(program: Program): void {
    this.selectedProgram.set(program);
    this.showModal.set(true);
  }

  protected onRowDelete(program: Program): void {
    this.programToDelete.set(program);
  }

  protected confirmDelete(): void {
    const program = this.programToDelete();
    if (!program) return;
    this.programToDelete.set(null);
    this.programService
      .deleteProgram(program.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.load() });
  }

  protected closeModal(): void {
    this.showModal.set(false);
    this.selectedProgram.set(null);
  }

  protected onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.pageIndex.set(0);
    this.load();
  }
}
